import { ref, computed } from 'vue';
import apiService from '@/services/api.service';

/**
 * useFilters — shared filter state + API fetching for filter dropdowns.
 *
 * Each call to useFilters() creates an independent reactive instance, so
 * components that need isolated filter state (e.g. Analytics) each get
 * their own copy.
 */
export function useFilters() {
  // ─── Selected values ──────────────────────────────────────────────────────
  const selectedTrainers  = ref([]);
  const selectedJockeys   = ref([]);
  const selectedCourses   = ref([]);
  const selectedRegions   = ref([]);
  const selectedTypes     = ref([]);
  const selectedDistances = ref([]);
  const selectedGoings    = ref([]);
  const selectedSurfaces  = ref([]);
  const selectedAges      = ref([]);
  const selectedSexes     = ref([]);
  const selectedSires     = ref([]);

  // ─── Dropdown options (populated by API) ─────────────────────────────────
  const trainerOptions  = ref([]);
  const jockeyOptions   = ref([]);
  const courseOptions   = ref([]);
  const regionOptions   = ref([]);
  const typeOptions     = ref([]);
  const distanceOptions = ref([]);
  const goingOptions    = ref([]);
  const surfaceOptions  = ref([]);
  const ageOptions      = ref([]);
  const sexOptions      = ref([]);
  const sireOptions     = ref([]);

  // ─── Per-filter loading flags ─────────────────────────────────────────────
  const isLoadingTrainers  = ref(false);
  const isLoadingJockeys   = ref(false);
  const isLoadingCourses   = ref(false);
  const isLoadingRegions   = ref(false);
  const isLoadingTypes     = ref(false);
  const isLoadingDistances = ref(false);
  const isLoadingGoings    = ref(false);
  const isLoadingSurfaces  = ref(false);
  const isLoadingAges      = ref(false);
  const isLoadingSexes     = ref(false);
  const isLoadingSires     = ref(false);

  // ─── Search handler factory ───────────────────────────────────────────────
  // Debounce is already handled inside MultiSelectCombobox; these are called
  // only once the debounce has settled.
  const makeSearchFn = (apiFn, optionsRef, loadingRef) => (searchTerm) => {
    loadingRef.value = true;
    apiFn(searchTerm)
      .then(r  => { optionsRef.value = r.data || []; })
      .catch(e => { console.error('Filter fetch error:', e); })
      .finally(() => { loadingRef.value = false; });
  };

  const searchTrainers  = makeSearchFn(t => apiService.getTrainers(t),  trainerOptions,  isLoadingTrainers);
  const searchJockeys   = makeSearchFn(t => apiService.getJockeys(t),   jockeyOptions,   isLoadingJockeys);
  const searchCourses   = makeSearchFn(t => apiService.getCourses(t),   courseOptions,   isLoadingCourses);
  const searchRegions   = makeSearchFn(t => apiService.getRegions(t),   regionOptions,   isLoadingRegions);
  const searchTypes     = makeSearchFn(t => apiService.getTypes(t),     typeOptions,     isLoadingTypes);
  const searchDistances = makeSearchFn(t => apiService.getDistances(t), distanceOptions, isLoadingDistances);
  const searchGoings    = makeSearchFn(t => apiService.getGoings(t),    goingOptions,    isLoadingGoings);
  const searchSurfaces  = makeSearchFn(t => apiService.getSurfaces(t),  surfaceOptions,  isLoadingSurfaces);
  const searchAges      = makeSearchFn(t => apiService.getAges(t),      ageOptions,      isLoadingAges);
  const searchSexes     = makeSearchFn(t => apiService.getSexes(t),     sexOptions,      isLoadingSexes);
  const searchSires     = makeSearchFn(t => apiService.getSires(t),     sireOptions,     isLoadingSires);

  // ─── Serialized params (ready to send to API) ─────────────────────────────
  // Trainers/Jockeys/Courses/Sires have an `id` field; the rest use `value`.
  const filterParams = computed(() => ({
    Trainers:  selectedTrainers.value.map(t => t.id),
    Jockeys:   selectedJockeys.value.map(j => j.id),
    Courses:   selectedCourses.value.map(c => c.id),
    Regions:   selectedRegions.value.map(r => r.value),
    Types:     selectedTypes.value.map(t => t.value),
    Distances: selectedDistances.value.map(d => d.value),
    Goings:    selectedGoings.value.map(g => g.value),
    Surfaces:  selectedSurfaces.value.map(s => s.value),
    Ages:      selectedAges.value.map(a => a.value),
    Sexes:     selectedSexes.value.map(s => s.value),
    Sires:     selectedSires.value.map(s => s.value), // backend filters by sire name (string)
  }));

  // ─── Active filter count (total selections across all filters) ────────────
  const activeFilterCount = computed(() =>
    selectedTrainers.value.length  + selectedJockeys.value.length  +
    selectedCourses.value.length   + selectedRegions.value.length  +
    selectedTypes.value.length     + selectedDistances.value.length +
    selectedGoings.value.length    + selectedSurfaces.value.length +
    selectedAges.value.length      + selectedSexes.value.length    +
    selectedSires.value.length
  );

  // Per-section counts (used for accordion header badges)
  const basicFilterCount = computed(() =>
    selectedTrainers.value.length + selectedJockeys.value.length +
    selectedCourses.value.length  + selectedRegions.value.length +
    selectedTypes.value.length
  );

  const advancedFilterCount = computed(() =>
    selectedDistances.value.length + selectedGoings.value.length   +
    selectedSurfaces.value.length  + selectedAges.value.length     +
    selectedSexes.value.length     + selectedSires.value.length
  );

  // ─── Clear all selections ─────────────────────────────────────────────────
  const clearAll = () => {
    selectedTrainers.value  = [];
    selectedJockeys.value   = [];
    selectedCourses.value   = [];
    selectedRegions.value   = [];
    selectedTypes.value     = [];
    selectedDistances.value = [];
    selectedGoings.value    = [];
    selectedSurfaces.value  = [];
    selectedAges.value      = [];
    selectedSexes.value     = [];
    selectedSires.value     = [];
  };

  return {
    // Selections
    selectedTrainers, selectedJockeys, selectedCourses, selectedRegions,
    selectedTypes, selectedDistances, selectedGoings, selectedSurfaces,
    selectedAges, selectedSexes, selectedSires,
    // Options
    trainerOptions, jockeyOptions, courseOptions, regionOptions,
    typeOptions, distanceOptions, goingOptions, surfaceOptions,
    ageOptions, sexOptions, sireOptions,
    // Loading flags
    isLoadingTrainers, isLoadingJockeys, isLoadingCourses, isLoadingRegions,
    isLoadingTypes, isLoadingDistances, isLoadingGoings, isLoadingSurfaces,
    isLoadingAges, isLoadingSexes, isLoadingSires,
    // Search handlers
    searchTrainers, searchJockeys, searchCourses, searchRegions,
    searchTypes, searchDistances, searchGoings, searchSurfaces,
    searchAges, searchSexes, searchSires,
    // Derived
    filterParams,
    activeFilterCount,
    basicFilterCount,
    advancedFilterCount,
    clearAll,
  };
}
